import { useLocation } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { catalogApi } from '../../services/CatalogService';
import MetaTags from 'react-meta-tags';
import { Header } from './header';
import { CategorySection } from './category';
import { StickyCategoryBar } from './stickyCategoryBar';
import { ProductCard } from './productCard';
import { CartSidebar } from './cartSidebar';
import CatalogLoader from '../../components/Loaders/catalogLoader';
import { buildCartItem } from './utils/buildCartItems';
import { FloatingCartButton } from './floatingCartBtn';
import { SearchBar } from './searchBar';

import { ProductCardSkeleton } from './loader/ProductCardSkeleton';
import { HeaderSkeleton } from './loader/HeaderSkeleton';
import { CategorySkeleton } from './loader/CategorySkeleton';
import { orderCompleted } from '../../helpers/currency_helper';
import styles from './styles/Catalog.module.css';
import Boxing from '../../assets/animation/boxing.gif';

export default function Catalog({ profileData, loading }) {
  const location = useLocation();
  const path = location.pathname;
  // const order_id = path.split('order_id=')[1];
  const order_id = path.replace('/catalog/', '');
  const { data = [], isLoading, error, refetch } = catalogApi.useFetchProductsQuery(order_id);

  const [catalogProducts, setCatalogData] = useState([]);
  const [order, setOrderData] = useState([]);
  const [categories, setCategoriesData] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [progress, setProgress] = useState({ step: 'Fetching data from DB...', time: 10 });
  const [isCartHydrated, setIsCartHydrated] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showSticky, setShowSticky] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const categoryRef = useRef();
  const progressUpdatedRef = useRef(false);
  const timeoutsRef = useRef([]);

  useEffect(() => {
    const rootElement = document.getElementById('root');
    const hasCustomView = rootElement.querySelector('.custom-view');
    rootElement.style.overflow = hasCustomView ? 'visible' : 'hidden';
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!data) return;

    let newData = JSON.parse(JSON.stringify(data));

    if (newData.products?.length > 0) setCatalogData([...newData.products]);
    if (newData.order?.length > 0) setOrderData([...newData.order]);

    if (newData.categories?.length > 0) {
      let fullCategories = newData.categories;
      let selectedCategoryIds = newData.order?.[0]?.categories || [];

      if (selectedCategoryIds.length > 0) {
        const categoryIdSet = new Set(selectedCategoryIds);

        const matchedCategories = fullCategories.filter((cat) => {
          if (!cat._id) return false;
          return categoryIdSet.has(cat._id);
        });

        if (matchedCategories.length > 0) {
          matchedCategories.unshift({
            sortNo: 0,
            catNameLng: {
              catName: "All",
              catNameRu: "All",
              catNameSp: "All",
              catNameCn: "All",
              catNameFr: "All",
              catNamePor: "All",
              catNameJa: "All"
            }
          });
          setCategoriesData(matchedCategories);
        } else {
          fullCategories.unshift({
            sortNo: 0,
            catNameLng: {
              catName: "All",
              catNameRu: "All",
              catNameSp: "All",
              catNameCn: "All",
              catNameFr: "All",
              catNamePor: "All",
              catNameJa: "All"
            }
          });
          setCategoriesData(fullCategories);
        }
      } else {
        fullCategories.unshift({
          sortNo: 0,
          catNameLng: {
            catName: "All",
            catNameRu: "All",
            catNameSp: "All",
            catNameCn: "All",
            catNameFr: "All",
            catNamePor: "All",
            catNameJa: "All"
          }
        });
        setCategoriesData(fullCategories);
      }
    }
  }, [data]);

  useEffect(() => {
    if (isLoading && !progressUpdatedRef.current) {
      progressUpdatedRef.current = true;
      setProgress({ step: 'Fetching data from DB...', time: 30 });

      timeoutsRef.current.push(
        setTimeout(() => {
          setProgress({ step: 'Loading Pictures...', time: 70 });
        }, 2000)
      );
    }

    if (!isLoading && data && progressUpdatedRef.current) {
      setProgress({ step: 'Completed.', time: 100 });

      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];

      setTimeout(() => {
        progressUpdatedRef.current = false;
      }, 1000);
    }

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [isLoading]);

  useEffect(() => {
    if (!order || order.length === 0 || isLoading || catalogProducts.length === 0) return;

    const currentOrder = order[0];
    const storageKey = `cartItems_${currentOrder._id}`;
    const storedCart = localStorage.getItem(storageKey) || currentOrder?.orderInfo;

    if (storedCart) {
      try {
        const parsed = typeof storedCart === 'string' ? JSON.parse(storedCart) : storedCart;

        const hydrated = parsed
          .map(({ _id, quantity, nomenclatureCode }) => {
            const product = catalogProducts.find((p) => p._id === _id);
            if (!product) return null;

            let selectedComposition = '';
            let selectedIndex = 0;
            // Check if nomenclatureCode includes a suffix like "_30G_13mm"
            if (nomenclatureCode && nomenclatureCode.includes('_')) {
              const parts = nomenclatureCode.split('_');
              const suffix = parts.slice(1).join('_'); // "30G_13mm"
              selectedComposition = suffix.replace(/_/g, ' '); // => "30G 13mm"

              // Match composition to get index
              const parsedComposition = parseMesoneedleComposition(product.languages?.[currentOrder.language]?.composition || '');
              selectedIndex = parsedComposition.findIndex((c) => c === selectedComposition);
              if (selectedIndex === -1) selectedIndex = 0; // fallback
            }

            return buildCartItem(product, quantity, currentOrder, selectedComposition, selectedIndex);
          })
          .filter(Boolean);

        setCartItems(hydrated);
        setIsCartHydrated(true);
      } catch (err) {
        console.error('Error parsing cartItems from localStorage:', err);
        setIsCartHydrated(true);
      }
    } else {
      setIsCartHydrated(true);
    }
  }, [isLoading, catalogProducts, order]);

  useEffect(() => {
    if (!isCartHydrated || !order || order.length === 0) return;

    const storageKey = `cartItems_${order[0]._id}`;
    const compactCart = cartItems.map((item) => ({
      _id: item._id,
      nomenclatureCode: item.nomenclatureCode,
      quantity: item.quantity
    }));

    if (compactCart.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(compactCart));
    } else {
      localStorage.removeItem(storageKey);
    }
  }, [cartItems, isCartHydrated, order]);

  useEffect(() => {
    if (cartItems.length === 0) setShowCart(false);
  }, [cartItems]);

  useEffect(() => {
    document.body.style.overflow = showCart ? 'hidden' : '';
  }, [showCart]);

  useEffect(() => {
    if (!categoryRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowSticky(entry.intersectionRatio < 1);
      },
      { threshold: 1.0 }
    );

    observer.observe(categoryRef.current);

    return () => observer.disconnect();
  }, [categoryRef.current]);

  const parseMesoneedleComposition = (composition) => {
    const regex = /(\d{2}G)\s*\(([^)]+)\)/g;
    let match;
    const result = [];

    while ((match = regex.exec(composition)) !== null) {
      const gauge = match[1];
      const sizes = match[2].split(',').map((s) => s.trim());
      result.push(...sizes.map((size) => `${gauge} ${size}`));
    }

    return result;
  };

  const handleAddToCart = (product, quantity = 1, selectedComposition = '', selectedIndex = 0) => {
    if (!order || order.length === 0) return;

    const cartItem = buildCartItem(product, quantity, order[0], selectedComposition, selectedIndex);
    if (!cartItem) return;

    setCartItems((prev) => {
      const existing = prev.find((item) => item.nomenclatureCode === cartItem.nomenclatureCode);
      if (existing) {
        return prev.map((item) => (item.nomenclatureCode === cartItem.nomenclatureCode ? { ...item, quantity: item.quantity + quantity } : item));
      }
      return [...prev, cartItem];
    });
  };

  const handleCategorySelect = (categoryName) => {
    if (categoryName === 'All') {
      setSelectedCategories([]); // Clear all filters
      return;
    }

    setSelectedCategories(
      (prev) =>
        prev.includes(categoryName)
          ? prev.filter((cat) => cat.catNameLng?.catName !== categoryName) // remove
          : [...prev, categoryName] // add
    );
  };
  const filteredProducts = catalogProducts
    .filter((p) => selectedCategories.length === 0 || selectedCategories.includes(p.category?.catNameLng?.catName))
    .filter((p) => {
      const lang = p.languages[order[0].language];
      const titleMatch = lang?.title?.toLowerCase().includes(searchTerm.toLowerCase());
      const descriptionMatch = lang?.description?.toLowerCase().includes(searchTerm.toLowerCase());
      return titleMatch || descriptionMatch;
    });
  console.log(categories);
  return (
    <div className='bg-white min-vh-100 custom-view'>
      {(isLoading || progress.time < 100) && (
        <>
          <HeaderSkeleton />
          <div className='container'>
            <CategorySkeleton />

            <div className='row'>
              {[...Array(8)].map((_, index) => (
                <div className='col-12 col-md-12 col-lg-6' key={index}>
                  <ProductCardSkeleton />
                </div>
              ))}
            </div>
          </div>
          <CatalogLoader progress={progress} isLoading={isLoading} />
        </>
      )}

      {!isLoading &&
        (order && order.length > 0 && order[0].orderStatus === false ? (
          <>
            <MetaTags>
              <title>Catalog Products</title>
            </MetaTags>
            <Header order={order} cartItems={cartItems} onToggleCart={setShowCart} />
            <div className='container'>
              <div ref={categoryRef}>
                <CategorySection language={order[0].language} categories={categories} onCategorySelect={handleCategorySelect} selectedCategories={selectedCategories} />
              </div>

              <StickyCategoryBar show={showSticky} categories={categories} onCategorySelect={handleCategorySelect} selectedCategories={selectedCategories} />

              <FloatingCartButton show={showSticky} onClick={() => setShowCart(true)} itemCount={cartItems.length} />

              <div className='container'>
                <SearchBar value={searchTerm} onChange={setSearchTerm} />
                <div className='row'>
                  {filteredProducts.map((product) => (
                    <div className='col-12 col-md-12 col-lg-6' key={product._id}>
                      <ProductCard
                        image={product.pic}
                        category={product.category[order[0].language === 'eng' ? 'catName' : `catName${order[0].language.charAt(0).toUpperCase()}${order[0].language.slice(1)}`]}
                        nomenclatureCode={product.nomenclatureCode}
                        title={product.languages[order[0].language].title}
                        description={product.languages[order[0].language].description}
                        composition={product.languages[order[0].language].composition}
                        language={order[0].language}
                        needle_gage={product.languages[order[0].language].needle_gage}
                        needle_mm={product.languages[order[0].language].needle_mm}
                        needle_type={product.languages[order[0].language].needle_type}
                        thread_gage={product.languages[order[0].language].thread_gage}
                        thread_mm={product.languages[order[0].language].thread_mm}
                        thread_type={product.languages[order[0].language].thread_type}
                        includeAddWPrice={order[0]?.includeAddWPrice}
                        includeWPrice={order[0]?.includeWPrice}
                        region={order[0].country}
                        price_r={order[0].currency !== '' ? product.prices[order[0].currency]?.price_r : product.regions[order[0].country]?.price_r}
                        price_w={order[0].currency !== '' ? product.prices[order[0].currency]?.price_w : product.regions[order[0].country]?.price_w}
                        price_w_add={order[0].currency !== '' ? product.prices[order[0].currency]?.price_w_add : product.regions[order[0].country]?.price_w_add}
                        r_amount={product.r_amount}
                        w_amount={product.w_amount}
                        w_amount_add={product.w_amount_add}
                        onAddToCart={(qty, selectedComposition) => handleAddToCart(product, qty, selectedComposition)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className={`${styles.overlay} ${showCart ? styles.visible : styles.hidden}`} onClick={() => setShowCart(false)} />
              <CartSidebar order={order} items={cartItems} setItems={setCartItems} onClose={() => setShowCart(false)} isOpen={showCart} />
            </div>
          </>
        ) : (
          // <div className={styles.fullScreenContainerSubmitted}>
          //   <div class='alert alert-success' role='alert'>
          //     <h2 className={styles.titleSubmitted}>Order Submitted!</h2>
          //     <p className={styles.messageSubmitted}>Your order has been successfully submitted. Please contact your manager to receive a new ordering link.</p>
          //   </div>
          // </div>

          <div className={styles.fullScreenContainerSubmitted}>
            <img
              src={Boxing} // or use an external URL
              alt='Order Submitted'
              className={styles.gif}
            />
            <h2 className={styles.titleSubmitted}>{(orderCompleted[order[0]?.language] || orderCompleted['eng'])[0]}</h2>
            <p className={styles.messageSubmitted}>{(orderCompleted[order[0]?.language] || orderCompleted['eng'])[1]}</p>
            <p className={styles.messageSubmitted}>{(orderCompleted[order[0]?.language] || orderCompleted['eng'])[2]}</p>
          </div>
        ))}
    </div>
  );
}
